import 'primeflex/primeflex.css';

import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { toaster } from 'components/v2/toast';
import { ProcessStatus } from 'constants/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { SettingsIcon } from 'images/icons/svg';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Panel, PanelHeaderTemplateOptions } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useContext, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { CompanyListModel } from 'services/EpdClient';
import { getCollocationCenterCompanies } from 'services/api/epdCollocationCentersApi';
import { useDeleteCollocationCenter } from 'services/api/mutations';
import styled from 'styled-components';
import { CollocationCenterModel } from 'types/types';

type TProps = {
  collocationCenter: CollocationCenterModel;
  handleEditCollocationCenter: () => void;
};

const CollocationCenterItem = ({ collocationCenter, handleEditCollocationCenter }: TProps) => {
  const { changeCompany } = useContext(CompanyContext);
  const menu = useRef<Menu>(null);
  const [data, setData] = useState<CompanyListModel[]>([]);
  const [status, setStatus] = useState<ProcessStatus>(ProcessStatus.None);
  const deleteMutation = useDeleteCollocationCenter();

  const fetchData = async () => {
    setStatus(ProcessStatus.Fetching);
    try {
      const result = await getCollocationCenterCompanies(collocationCenter.id!);
      setData(result);
      setStatus(ProcessStatus.Success);
    } catch (error) {
      console.error(error);
      setStatus(ProcessStatus.Error);
    }
  };

  const acceptCollocationCenterDelete = async () => {
    deleteMutation.mutate(collocationCenter.id!, {
      onSuccess: () => {
        toaster({ severity: 'success', summary: 'Success', details: 'Co-location centre has been deleted' });
      },
      onError: () => {
        toaster({ severity: 'error', summary: 'Error', details: 'Co-location centre deletion error' });
      },
    });
  };

  const items: MenuItem[] = [
    {
      label: 'Edit CLC',
      command: handleEditCollocationCenter,
    },
    {
      label: 'Delete CLC',
      command: () => {
        confirmDelete({
          caption: 'Are you sure you want to delete the co-location centre?',
          description: 'This action cannot be undone',
          onConfirm: () => {
            acceptCollocationCenterDelete();
          },
        });
      },
    },
  ];

  const template = (options: PanelHeaderTemplateOptions) => {
    const className = `${options.className} justify-content-between border-none `;
    const titleClassName = `${options.titleClassName} ml-2`;
    return (
      <div className={className} style={{ backgroundColor: '#FFFFFF' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className={titleClassName}>{collocationCenter.name}</span>
          <ToggleButton options={options} />
        </div>
        <div>
          <Menu model={items} popup ref={menu} />
          <Button className="border-none surface-section text-gray-900" onClick={(e) => menu?.current?.toggle(e)}>
            <i className="pi pi-ellipsis-v font-bold text-2xl" style={{ color: '#1E6052' }} />
          </Button>
        </div>
      </div>
    );
  };

  const settingsLinkTemplate = (company: CompanyListModel) => {
    return (
      <Link
        to={EpdLinks.manageCompany(company.id || '')}
        style={{ padding: '1rem' }}
        onClick={() => changeCompany && changeCompany(company.id)}
      >
        <Image alt="cog" src={SettingsIcon} />
      </Link>
    );
  };

  return (
    <Panel className="border-none mb-2" headerTemplate={template} toggleable collapsed onExpand={fetchData}>
      {status == ProcessStatus.Fetching ? (
        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
      ) : (
        <DataTable
          value={data}
          paginator
          rows={5}
          paginatorTemplate="PrevPageLink PageLinks NextPageLink CurrentPageReport RowsPerPageDropdown "
          currentPageReportTemplate="{first}-{last} of {totalRecords}"
          rowsPerPageOptions={[5, 10, 25, 50]}
          tableStyle={{ minWidth: '49rem' }}
        >
          <Column field="name" header="Name" sortable style={{ minWidth: '25%' }}></Column>
          <Column field="homeCountryName" sortable header="Country" style={{ minWidth: '25%' }}></Column>
          <Column field="website" header="Website" style={{ minWidth: '25%' }}></Column>
          <Column header="Settings" body={settingsLinkTemplate} style={{ maxWidth: '5rem' }}></Column>
        </DataTable>
      )}
    </Panel>
  );
};

const Image = styled.img`
  height: 1rem;
  vertical-align: top;
  cursor: pointer;
`;

export default CollocationCenterItem;
